import "./transcript-panel.scss";

import { BindingEngine, bindingMode, Disposable } from "aurelia-binding";
import { autoinject, bindable, customElement } from "aurelia-framework";

import { TranscriptPanelState } from "./transcript-panel-state";
import { ITranscript } from "../../../interfaces/audio/i-audio";

@customElement("transcript-panel")
@autoinject
export class TranscriptPanel {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public hasTranscriptOpen: boolean = false;
    private _state: TranscriptPanelState;
    private _element: HTMLElement;
    private _bindingEngine: BindingEngine;
    private _parent: HTMLElement;
    private _subscriptions: Disposable[] = [];

    public constructor(state: TranscriptPanelState, element: Element, bindingEngine: BindingEngine) {
        this._state = state;
        this._element = element as HTMLElement;
        this._bindingEngine = bindingEngine;
    }

    public attached() {
        this._parent = this._element.parentElement;
        this._subscriptions = [
            this._bindingEngine.propertyObserver(this._state, "isOpen").subscribe(() => this.update()),
            this._bindingEngine.propertyObserver(this._state, "isExpanded").subscribe(() => this.update())
        ];
        window.addEventListener("resize", this.handleResize);
        this.update();
    }

    public detached() {
        this._subscriptions.forEach((subscription) => subscription.dispose());
        this._subscriptions = [];
        window.removeEventListener("resize", this.handleResize);
        this._parent?.classList.remove("is-transcript-open");
        this._parent = null;
    }

    private handleResize = () => this.updatePosition();

    private update() {
        this._element?.classList.toggle("is-open", this._state.isOpen);
        this._element?.classList.toggle("is-expanded", this._state.isExpanded);
        this._parent?.classList.toggle("is-transcript-open", this._state.isOpen && !this._state.isExpanded);
        this.hasTranscriptOpen = this._state.isOpen;
        this.updatePosition();
    }

    private updatePosition() {
        if (!this._parent || !this._state.isOpen) {
            return;
        }
        const rect = this._parent.getBoundingClientRect();
        const style = this._element.style;

        const pageTitle = this._parent.querySelector("#Note__Info");
        const top = this._state.isExpanded && pageTitle ? pageTitle.getBoundingClientRect().bottom : rect.top;
        style.top = `${top}px`;
        style.bottom = `${window.innerHeight - rect.bottom}px`;
        style.right = `${window.innerWidth - rect.right}px`;
        style.left = this._state.isExpanded ? `${rect.left}px` : "";
    }

    public get isExpanded(): boolean {
        return this._state.isExpanded;
    }

    public get transcriptData(): ITranscript {
        return this._state.transcript;
    }

    public toggleExpanded() {
        this._state.toggleExpanded();
    }

    public close() {
        this._state.close();
    }
}